/**
 * Headless mode (-p) — pipe-friendly coding agent execution
 *
 * Writes Agent output directly to stdout (raw, no chalk wrapping).
 * Status, errors, and diff go to stderr.
 * Supports --output-format json for structured machine-readable output.
 * Designed for scripting: `spacecode -p "prompt" -R repo > output.txt`
 */

import type { ProxyMode, HeadlessResult } from '@spacecode/shared/remote';
import { validateRepo, validateBranch, validatePrompt } from '../utils/validation.js';
import { generateTaskId } from '../utils/task.js';
import { setupRun } from '../utils/run-setup.js';
import { captureException, logError } from '../posthog.js';
import { buildRemoteTask, statusFromResult, errorTaskStatus } from '../utils/remote-task.js';

export type OutputFormat = 'text' | 'json';

export interface HeadlessOptions {
  repo: string;
  prompt: string;
  branch?: string;
  noCommit?: boolean;
  idleTimeout?: number;
  proxyMode?: ProxyMode;
  outputFormat?: OutputFormat;
  verbose?: boolean;
}

export async function headless(options: HeadlessOptions): Promise<void> {
  const taskId = generateTaskId();
  const validRepo = validateRepo(options.repo);
  const validPrompt = validatePrompt(options.prompt);
  const validBranch = options.branch ? validateBranch(options.branch) : undefined;
  const verbose = options.verbose ?? false;
  const startTime = Date.now();

  // SIGINT → abort the SSE stream (distinct from keepalive timeout)
  const abortController = new AbortController();
  let abortReason: 'sigint' | 'keepalive' | null = null;
  const sigintHandler = () => {
    abortReason = 'sigint';
    abortController.abort();
  };
  process.on('SIGINT', sigintHandler);

  const jsonMode = options.outputFormat === 'json';
  let finalExitCode = 1;

  // JSON mode: buffer output, emit structured object at the end
  const outputChunks: string[] = [];
  let resultDiff = '';

  try {
    const { client, envVars } = await setupRun({ proxyMode: options.proxyMode });

    let exitCode = 0;
    let success = true;

    // Keepalive: abort if no SSE events for 5 minutes
    const KEEPALIVE_TIMEOUT_MS = 5 * 60 * 1000;
    let keepaliveTimer: ReturnType<typeof setTimeout> | null = null;

    const resetKeepalive = () => {
      if (keepaliveTimer) clearTimeout(keepaliveTimer);
      keepaliveTimer = setTimeout(() => {
        process.stderr.write('error: stream stalled (no data for 5 minutes)\n');
        abortReason = 'keepalive';
        abortController.abort();
      }, KEEPALIVE_TIMEOUT_MS);
    };

    resetKeepalive();

    try {
      await client.run(
        {
          repo: validRepo,
          prompt: validPrompt,
          envVars,
          branch: validBranch,
          noCommit: options.noCommit,
          idleTimeoutMinutes: options.idleTimeout,
        },
        (event) => {
          resetKeepalive();
          switch (event.type) {
            case 'status':
              if (verbose) {
                const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
                process.stderr.write(`[${event.data.phase}] +${elapsed}s\n`);
              } else {
                process.stderr.write(`[${event.data.phase}]\n`);
              }
              break;
            case 'output':
              if (jsonMode) {
                outputChunks.push(event.data.text);
              } else if (verbose && event.data.stream === 'stderr') {
                process.stderr.write(`[stderr] ${event.data.text}`);
              } else {
                process.stdout.write(event.data.text);
              }
              break;
            case 'done':
              exitCode = event.data.exitCode;
              success = event.data.success;
              resultDiff = event.data.diff;
              if (!jsonMode && event.data.diff) {
                process.stderr.write(event.data.diff + '\n');
              }
              break;
            case 'error':
              process.stderr.write(`error: ${event.data.message}\n`);
              success = false;
              exitCode = 1;
              break;
            default: {
              const _exhaustive: never = event;
              void _exhaustive;
            }
          }
        },
        abortController.signal,
      );
    } finally {
      if (keepaliveTimer) clearTimeout(keepaliveTimer);
    }

    // JSON mode: emit structured result to stdout
    if (jsonMode) {
      const result: HeadlessResult = {
        taskId,
        repo: validRepo,
        branch: validBranch,
        result: outputChunks.join(''),
        exitCode,
        success,
        diff: resultDiff,
        durationSeconds: Math.round((Date.now() - startTime) / 1000),
      };
      process.stdout.write(JSON.stringify(result, null, 2) + '\n');
    }

    // Save task record (non-fatal — don't let save failure turn success into error)
    try {
      const { addTask } = await import('../config.js');
      const task = buildRemoteTask({
        id: taskId,
        repo: validRepo,
        prompt: validPrompt,
        mode: 'headless',
        status: statusFromResult(success),
        exitCode,
        branch: validBranch,
      });
      await addTask(task);
    } catch (saveErr) {
      process.stderr.write(`warning: failed to save task record: ${saveErr instanceof Error ? saveErr.message : String(saveErr)}\n`);
    }

    finalExitCode = exitCode;
  } catch (error) {
    const isAbort = error instanceof Error && error.name === 'AbortError';
    const isSigint = isAbort && abortReason === 'sigint';
    const isKeepalive = isAbort && abortReason === 'keepalive';

    const errorExitCode = isSigint ? 130 : 1;
    const errorMessage = isSigint
      ? 'Cancelled by user'
      : isKeepalive
        ? 'Stream stalled (no data for 5 minutes)'
        : (error instanceof Error ? error.message : String(error));
    const taskStatus = errorTaskStatus(isSigint);

    // JSON mode: emit error result
    if (jsonMode) {
      const result: HeadlessResult = {
        taskId,
        repo: validRepo,
        branch: validBranch,
        result: outputChunks.join(''),
        exitCode: errorExitCode,
        success: false,
        diff: resultDiff,
        durationSeconds: Math.round((Date.now() - startTime) / 1000),
        error: errorMessage,
      };
      process.stdout.write(JSON.stringify(result, null, 2) + '\n');
    }

    // Save task record
    try {
      const { addTask } = await import('../config.js');
      const task = buildRemoteTask({
        id: taskId,
        repo: validRepo,
        prompt: validPrompt,
        mode: 'headless',
        status: taskStatus,
        error: errorMessage,
      });
      await addTask(task);
    } catch (saveErr) {
      process.stderr.write(`warning: failed to save task record: ${saveErr instanceof Error ? saveErr.message : String(saveErr)}\n`);
    }

    if (!jsonMode && !isAbort) {
      process.stderr.write(`error: ${errorMessage}\n`);
    }

    // Report non-abort errors to Sentry
    if (!isAbort && error instanceof Error) {
      captureException(error, { command: 'headless', repo: validRepo, taskId });
      logError('headless task failed', { taskId, repo: validRepo, error: errorMessage });
    }

    finalExitCode = errorExitCode;
  } finally {
    process.removeListener('SIGINT', sigintHandler);
  }

  process.exit(finalExitCode);
}
